import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Plus, SquarePen, Trash2 } from "lucide-react";

import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import Table from "../../Components/Table";
import TextArea from "../../Components/TextArea";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  createOrganizationLevel,
  deleteOrganizationLevel,
  fetchOrganizationLevels,
  fetchRelationshipLevels,
  updateOrganizationLevel,
  updateRelationshipLevel,
} from "../../Store/Reducers/AdminSlice";
import DeleteModal from "../DeleteModal";
import FormModal from "../FormModal";

interface Level {
  id?: string | number;
  level_code: string;
  name: string;
  description: string;
  guidance?: string;
}

interface LevelFormData {
  id?: string;
  level_code: string;
  name: string;
  description: string;
  guidance: string;
}

type LevelKind = "al" | "rl";
type ModalMode = "add" | "edit";

const AL_MAX_LEVELS = 10;
const AL_MIN_LEVELS = 6;

const emptyFormData = (): LevelFormData => ({
  level_code: "",
  name: "",
  description: "",
  guidance: "",
});

interface AdminLevelContainerProps {
  loading?: boolean;
}

const AdminLevelContainer: React.FC<AdminLevelContainerProps> = ({
  loading = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    organizationLevels,
    relationshipLevels,
    relationshipLevelsLoading,
    createLevelLoading,
    deleteLevelLoading,
    updateRelationshipLevelLoading,
  } = useAppSelector(state => state.admin);

  const [activeKind, setActiveKind] = useState<LevelKind>("al");
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [formData, setFormData] = useState<LevelFormData>(emptyFormData());

  useEffect(() => {
    if (organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
    dispatch(fetchRelationshipLevels());
  }, [dispatch, organizationLevels.length]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setActiveKind("al");
    setModalMode("add");
    setSelectedLevel(null);
    setFormData(emptyFormData());
    setIsFormModalOpen(true);
  };

  const openEditModal = (kind: LevelKind, level: Level) => {
    setActiveKind(kind);
    setModalMode("edit");
    setSelectedLevel(level);
    setFormData({
      id: String(level.id),
      level_code: level.level_code,
      name: level.name,
      description: level.description,
      guidance: level.guidance || "",
    });
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (level: Level) => {
    setActiveKind("al");
    setSelectedLevel(level);
    setIsDeleteModalOpen(true);
  };

  const refreshLevels = useCallback(
    (kind: LevelKind) => {
      if (kind === "al") {
        dispatch(fetchOrganizationLevels());
      } else {
        dispatch(fetchRelationshipLevels());
      }
    },
    [dispatch]
  );

  const handleFormSubmit = async () => {
    if (activeKind === "al") {
      if (modalMode === "add") {
        await dispatch(createOrganizationLevel(formData));
      } else if (selectedLevel?.id) {
        await dispatch(
          updateOrganizationLevel({
            id: String(selectedLevel.id),
            payload: formData,
          })
        );
      }
    } else if (selectedLevel?.id) {
      await dispatch(
        updateRelationshipLevel({
          id: String(selectedLevel.id),
          payload: {
            level_code: formData.level_code,
            name: formData.name,
            description: formData.description,
            guidance: formData.guidance,
          },
        })
      );
    }

    setIsFormModalOpen(false);
    refreshLevels(activeKind);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLevel?.id) return;

    await dispatch(deleteOrganizationLevel(String(selectedLevel.id)));
    setIsDeleteModalOpen(false);
    refreshLevels("al");
  };

  const buildAlColumns = useCallback(
    () => [
      {
        key: "level_code",
        title: "LEVEL CODE",
        render: (value: string) => (
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-purple-700">
            {value}
          </span>
        ),
        width: "110px",
      },
      {
        key: "name",
        title: "NAME",
        render: (value: string) => (
          <span className="text-sm font-semibold text-gray-900">{value}</span>
        ),
        width: "160px",
      },
      {
        key: "description",
        title: "DESCRIPTION",
        render: (value: string) => (
          <span className="block max-w-md text-sm leading-snug text-gray-700">
            {value || "-"}
          </span>
        ),
      },
      {
        key: "guidance",
        title: "RATIONALE GUIDANCE",
        render: (value: string) => (
          <span className="block max-w-xl text-sm leading-snug text-gray-700">
            {value || "-"}
          </span>
        ),
      },
      {
        key: "actions",
        title: "ACTIONS",
        width: "180px",
        textalign: "center" as const,
        className: "justify-center",
        render: (_: unknown, record: Level) => {
          const isDeleteDisabled = organizationLevels.length <= AL_MIN_LEVELS;
          return (
            <div className="flex w-full items-center justify-center gap-2">
              <button
                type="button"
                className="inline-flex items-center text-sm font-medium text-purple-600 transition-colors hover:text-purple-900"
                onClick={() => openEditModal("al", record)}
              >
                <SquarePen size={15} className="mr-1" />
                Edit
              </button>
              <button
                type="button"
                className={`inline-flex items-center text-sm font-medium transition-colors ${
                  isDeleteDisabled
                    ? "cursor-not-allowed text-gray-400 opacity-50"
                    : "text-red-600 hover:text-red-900"
                }`}
                onClick={() => !isDeleteDisabled && openDeleteModal(record)}
                disabled={isDeleteDisabled}
              >
                <Trash2 size={15} className="mr-1" />
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [organizationLevels.length]
  );

  const rlColumns = useMemo(
    () => [
      {
        key: "level_code",
        title: "LEVEL CODE",
        render: (value: string) => (
          <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-teal-800">
            {value}
          </span>
        ),
        width: "110px",
      },
      {
        key: "name",
        title: "NAME",
        render: (value: string) => (
          <span className="text-sm font-semibold text-gray-900">{value}</span>
        ),
        width: "160px",
      },
      {
        key: "description",
        title: "DESCRIPTION",
        render: (value: string) => (
          <span className="block max-w-md text-sm leading-snug text-gray-700">
            {value || "-"}
          </span>
        ),
      },
      {
        key: "guidance",
        title: "RATIONALE GUIDANCE",
        render: (value: string) => (
          <span className="block max-w-xl text-sm leading-snug text-gray-700">
            {value || "-"}
          </span>
        ),
      },
      {
        key: "actions",
        title: "ACTIONS",
        width: "100px",
        textalign: "center" as const,
        className: "justify-center",
        render: (_: unknown, record: Level) => (
          <div className="flex w-full items-center justify-center">
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-purple-600 transition-colors hover:text-purple-900"
              onClick={() => openEditModal("rl", record)}
            >
              <SquarePen size={15} className="mr-1" />
              Edit
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const alColumns = useMemo(() => buildAlColumns(), [buildAlColumns]);

  const isAl = activeKind === "al";
  const formLoading = isAl
    ? createLevelLoading
    : updateRelationshipLevelLoading;
  const modalTitle =
    modalMode === "add"
      ? "Add New Level (AL)"
      : isAl
        ? "Edit Level (AL)"
        : "Edit Level (RL)";
  const submitLabel = modalMode === "add" ? "Add Level" : "Update Level";

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-lg font-medium text-gray-900">
            Organization Levels
          </h1>
          <p className="mt-1 text-sm font-medium text-purple-600">
            {organizationLevels.length} / {AL_MAX_LEVELS} AL levels · Minimum
            required: {AL_MIN_LEVELS}
          </p>
        </div>
        <Button
          variant="primary"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium"
          icon={<Plus size={16} />}
          onClick={openAddModal}
        >
          Add Level
        </Button>
      </div>

      <section className="mb-5">
        <div className="mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Account Levels (AL)
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            XFACTOR™ customer alignment maturity — used in account-level
            assessments ({organizationLevels.length} levels)
          </p>
        </div>
        <Table
          columns={alColumns}
          data={organizationLevels}
          loading={loading}
        />
      </section>

      <section>
        <div className="mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Relationship Levels (RL)
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Vendor-customer relationship maturity — used in relationship
            assessments ({relationshipLevels.length} levels)
          </p>
        </div>
        <Table
          columns={rlColumns}
          data={relationshipLevels}
          loading={loading || relationshipLevelsLoading}
        />
      </section>

      <FormModal
        isOpen={isFormModalOpen}
        toggle={() => setIsFormModalOpen(false)}
        title={modalTitle}
        onSubmit={handleFormSubmit}
        submitLabel={submitLabel}
        isEditMode={modalMode === "edit"}
        loading={formLoading}
      >
        <CommonInput
          label="Level Code"
          name="level_code"
          value={formData.level_code}
          onChange={handleChange}
          placeholder={isAl ? "L8" : "RL2"}
          disabled={modalMode === "edit"}
          required
        />
        {modalMode === "edit" ? (
          <p className="-mt-2 mb-2 text-xs text-gray-500">
            Level code cannot be changed
          </p>
        ) : null}
        <CommonInput
          label="Level Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter level name"
          required
        />
        <TextArea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter level description"
          rows={4}
          required
        />
        <TextArea
          label="Rationale Guidance"
          name="guidance"
          value={formData.guidance}
          onChange={handleChange}
          placeholder="Shown to sponsors as a writing prompt when they select this level"
          rows={3}
        />
        <p className="-mt-2 text-xs text-gray-500">
          Shown to sponsors as a writing prompt when they select this level
        </p>
      </FormModal>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        toggle={() => setIsDeleteModalOpen(false)}
        title="Delete Level (AL)"
        entityName={`${selectedLevel?.level_code} - ${selectedLevel?.name}`}
        onConfirm={handleDeleteConfirm}
        loading={deleteLevelLoading}
      />
    </div>
  );
};

export default AdminLevelContainer;
